import { forEach, get, reduce, set, unset } from 'lodash';

import actions from 'state/constants';
import initialState from 'state/initialState';

// Update state when cart opens/closes
const setToggleCartReducer = (state, action) => ({
  ...state,
  isOpen: action.payload,
  breadcrumb: get(action, ['meta', 'targetSelector']),
});

const setCartViewReducer = (state, action) => ({
  ...state,
  view: action.payload,
});

const setCartMessageReducer = (state, action) => ({
  ...state,
  message: action.payload,
});

// Update state when getting new data from API
const setCartDataReducer = (state, action) => ({
  ...state,
  data: action.payload.cart,
});

// Update state when starting to add multiple new line items to cart
const addingItemsReducer = (state, action) => {
  const { items } = action.payload;

  // Keep a map of the ids being added ([id]: number of ajax requests adding the item)
  const currentNums = reduce(
    items,
    (result, quantity, id) => {
      const currentNum = get(state, ['itemsBeingAdded', id], 0);

      return { ...result, [id]: currentNum + 1 };
    },
    {}
  );

  return {
    ...state,
    itemsBeingAdded: {
      ...state.itemsBeingAdded,
      ...currentNums,
    },
  };
};

// Update state when finished adding multiple new line item to cart
const addedItemsReducer = (state, action) => {
  const { addedItems, error } = action.payload;
  const { itemsBeingAdded } = state;

  forEach(addedItems, (quantity, id) => {
    const currentNum = get(state, ['itemsBeingAdded', id], 0);
    if (currentNum > 1) {
      set(itemsBeingAdded, id, currentNum - 1);
    } else {
      unset(itemsBeingAdded, id);
    }
  });

  return setCartDataReducer(
    {
      ...state,
      itemsBeingAdded,
      errorMessage: error || null,
    },
    action
  );
};

// Update state when starting to update a line item
const updatingItemReducer = (state, action) => {
  const itemId = action.payload.variantId;
  const currentNum = get(state, ['itemsBeingUpdated', itemId], 0);

  return {
    ...state,
    itemsBeingUpdated: {
      ...state.itemsBeingUpdated,
      [itemId]: currentNum + 1,
    },
  };
};

// Update state when finished updating a line item
const updatedItemReducer = (state, action) => {
  const { updatedItem, error } = action.payload;
  const { itemsBeingUpdated } = state;

  const currentNum = get(state, ['itemsBeingUpdated', updatedItem], 0);
  if (currentNum > 1) {
    set(itemsBeingUpdated, updatedItem, currentNum - 1);
  } else {
    unset(itemsBeingUpdated, updatedItem);
  }

  return setCartDataReducer(
    {
      ...state,
      itemsBeingUpdated,
      errorMessage: error || null,
    },
    action
  );
};

// Update state when starting to remove a line item
const removingItemReducer = (state, action) => {
  const itemId = action.payload.variantId;
  const currentNum = get(state, ['itemsBeingRemoved', itemId], 0);

  return {
    ...state,
    itemsBeingRemoved: {
      ...state.itemsBeingRemoved,
      [itemId]: currentNum + 1,
    },
  };
};

// Update state when finished removing a line item
const removedItemReducer = (state, action) => {
  const { removedItem, error } = action.payload;
  const { itemsBeingRemoved } = state;

  const currentNum = get(state, ['itemsBeingRemoved', removedItem], 0);
  if (currentNum > 1) {
    set(itemsBeingRemoved, removedItem, currentNum - 1);
  } else {
    unset(itemsBeingRemoved, removedItem);
  }

  return setCartDataReducer(
    {
      ...state,
      itemsBeingRemoved,
      errorMessage: error || null,
    },
    action
  );
};

// Update state when starting to clear the cart
const clearingCartReducer = state => ({
  ...state,
  isBeingCleared: true,
});

// Update state when finished clearing the cart
const clearedCartReducer = (state, action) =>
  setCartDataReducer(
    {
      ...state,
      isBeingCleared: false,
      errorMessage: action.payload.error || null,
    },
    action
  );

// Update state when starting to initialize the cart
const initializingCartReducer = state => ({
  ...state,
  isBeingInitialized: true,
});

// Update state when finished initializing the cart
const initializedCartReducer = (state, action) =>
  setCartDataReducer(
    {
      ...state,
      isBeingInitialized: false,
    },
    action
  );

// Update state when starting to set logo free packaging and/or gift message
const updatingGiftingOptions = state => ({
  ...state,
  isSettingGiftingOptions: true,
});

// Update state when finished setting logo free packaging and/or gift message
const updateGiftingOptions = (state, action) =>
  setCartDataReducer(
    {
      ...state,
      isSettingGiftingOptions: false,
    },
    action
  );

const fetchingUpsellsReducer = state => ({
  ...state,
  upsells: {
    ...state.upsells,
    isBeingFetched: true,
  },
});

const fetchedUpsellsReducer = (state, action) => ({
  ...state,
  upsells: {
    ...state.upsells,
    data: get(action, ['payload', 'upsells'], {}),
    isBeingFetched: false,
  },
});

const addingDiscountsReducer = state => ({
  ...state,
  isSettingDiscounts: true,
});

const addedDiscountsReducer = (state, action) =>
  setCartDataReducer(
    {
      ...state,
      isSettingDiscounts: false,
      errorMessage: action?.payload?.error || null,
    },
    action
  );

const setReturnsInsuranceRenderedReducer = (state, action) => ({
  ...state,
  returnsInsuranceRendered: action.payload,
});

// Figure out how to update state based on incoming action type
export const reducer = (state = initialState.ajaxCart, action) => {
  switch (action.type) {
    case actions.INIT_CART_REQUEST:
      return initializingCartReducer(state, action);
    case actions.INIT_CART_SUCCESS:
      return initializedCartReducer(state, action);

    case actions.ADD_LINE_ITEMS_REQUEST:
      return addingItemsReducer(state, action);
    case actions.ADD_LINE_ITEMS_SUCCESS:
    case actions.ADD_LINE_ITEMS_FAILURE:
      return addedItemsReducer(state, action);

    case actions.UPDATE_LINE_ITEM_REQUEST:
      return updatingItemReducer(state, action);
    case actions.UPDATE_LINE_ITEM_SUCCESS:
    case actions.UPDATE_LINE_ITEM_FAILURE:
      return updatedItemReducer(state, action);

    case actions.UPDATE_LINE_ITEM_ATTRIBUTES_REQUEST:
      return updatingItemReducer(state, action);
    case actions.UPDATE_LINE_ITEM_ATTRIBUTES_SUCCESS:
    case actions.UPDATE_LINE_ITEM_ATTRIBUTES_FAILURE:
      return updatedItemReducer(state, action);

    case actions.REMOVE_LINE_ITEM_REQUEST:
      return removingItemReducer(state, action);
    case actions.REMOVE_LINE_ITEM_SUCCESS:
    case actions.REMOVE_LINE_ITEM_FAILURE:
      return removedItemReducer(state, action);

    case actions.CLEAR_CART_REQUEST:
      return clearingCartReducer(state, action);
    case actions.CLEAR_CART_SUCCESS:
    case actions.CLEAR_CART_FAILURE:
      return clearedCartReducer(state, action);

    case actions.SET_GIFTING_OPTIONS:
      return updatingGiftingOptions(state, action);
    case actions.SET_GIFTING_OPTIONS_SUCCESS:
    case actions.SET_GIFTING_OPTIONS_FAILURE:
      return updateGiftingOptions(state, action);

    case actions.TOGGLE_CART:
      return setToggleCartReducer(state, action);

    case actions.SET_CART_VIEW:
      return setCartViewReducer(state, action);

    case actions.SET_CART_MESSAGE:
      return setCartMessageReducer(state, action);

    case actions.FETCH_CART_UPSELLS_REQUEST:
      return fetchingUpsellsReducer(state, action);
    case actions.FETCH_CART_UPSELLS_SUCCESS:
    case actions.FETCH_CART_UPSELLS_FAILURE:
      return fetchedUpsellsReducer(state, action);

    case actions.ADD_DISCOUNT_REQUEST:
      return addingDiscountsReducer(state, action);
    case actions.ADD_DISCOUNT_SUCCCESS:
    case actions.ADD_DISCOUNT_FAILURE:
      return addedDiscountsReducer(state, action);

    case actions.SET_RETURNS_INSURANCE_RENDERED:
      return setReturnsInsuranceRenderedReducer(state, action);

    default:
      return state;
  }
};

export default reducer;
